import {
  OAUTH_PROVIDER_PARAM,
  OAUTH_RETRY_PARAM,
  SHELL_OAUTH_RETURN_URL,
} from "./auth/safe-next";

const RETURN_URL = new URL(SHELL_OAUTH_RETURN_URL);

/** The ONLY query params a deep link may forward into /auth/callback: the
 *  OAuth code-exchange vocabulary (Supabase appends code / error*) plus the
 *  params buildShellReturnUrl threads. This is a security boundary, not a
 *  convenience: /auth/callback's GET also serves the legacy email-OTP path
 *  (`token_hash` + `type` → verifyOtp), and ANY app on the device can fire
 *  medceptor:// URLs — verbatim forwarding would let a crafted deep link
 *  carrying an attacker's OTP log the victim into the attacker's account
 *  (session fixation). Never widen this list to pass token_hash/type.
 */
const FORWARDED_PARAMS = [
  "code",
  "error",
  "error_code",
  "error_description",
  "next",
  OAUTH_PROVIDER_PARAM,
  OAUTH_RETRY_PARAM,
] as const;

/** Translate the shell's OAuth-return deep link
 *  (`medceptor://oauth-return?code=...`) into the in-webview callback path
 *  (`/auth/callback?code=...`). Returns null for anything else — the
 *  appUrlOpen listener receives EVERY URL that opens the app, and a deep
 *  link with neither a code nor an error is not an OAuth return (rejecting
 *  it also stops junk deep links from force-navigating the webview). */
export function oauthReturnToCallbackPath(rawUrl: string): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.protocol !== RETURN_URL.protocol || url.host !== RETURN_URL.host) {
    return null;
  }
  if (!url.searchParams.has("code") && !url.searchParams.has("error")) {
    return null;
  }
  const forwarded = new URLSearchParams();
  for (const param of FORWARDED_PARAMS) {
    const value = url.searchParams.get(param);
    if (value !== null) forwarded.set(param, value);
  }
  return `/auth/callback?${forwarded}`;
}

/** The app.medceptor.com links that open the native app (universal links),
 *  as AASA path patterns. Kept to the flows that must END in the app:
 *  - `/auth/confirm` (and under it): the email confirmation / reset link. Opened
 *    in Safari it would sign the student in there, leaving the app signed
 *    out and their onboarding answers behind in the app.
 *  - `/invite/*`: a class invite, so a student joins inside the app.
 *  Anything else stays in Safari: a link meant for the web (e.g. /pricing,
 *  which the shell must not sell through) never gets pulled into the app. */
export const UNIVERSAL_LINK_PATHS = [
  "/auth/confirm",
  "/auth/confirm/*",
  "/invite/*",
] as const;

/** Translate a universal link the app was opened with into the in-webview
 *  path to load, or null when it isn't one of ours. `ownHost` is the host the
 *  webview is serving (`window.location.host`): iOS only delivers links for
 *  the domain in the app's entitlement, which is the domain the shell loads,
 *  so comparing against our own host needs no second copy of the domain.
 *
 *  Forwarding `/auth/confirm`'s token_hash is safe here where the custom
 *  scheme above is not: `/auth/confirm` is the click-to-confirm interstitial
 *  (nothing is verified on load, the student presses Confirm), and iOS only
 *  routes links on the domain we verified through the AASA file. Opening one
 *  in the app is the same as opening it in Safari, except the session lands
 *  where the student actually is. */
export function universalLinkToPath(
  rawUrl: string,
  ownHost: string,
): string | null {
  let url: URL;
  try {
    url = new URL(rawUrl);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.host !== ownHost) return null;
  const path = url.pathname;
  const matches =
    path === "/auth/confirm" ||
    path.startsWith("/auth/confirm/") ||
    /^\/invite\/[^/]+$/.test(path);
  return matches ? `${path}${url.search}` : null;
}

/** How long a repeat of the same universal link counts as a re-delivery of
 *  one event (the new document mounting right after our own navigation)
 *  rather than the student tapping the link again. */
export const UNIVERSAL_LINK_REPEAT_MS = 10_000;

/** Whether this delivery of `url` is an immediate repeat of the previous one
 *  and should be ignored. Unlike an OAuth code, an email or invite link is
 *  the SAME URL on every tap, and a later second tap is a real request (the
 *  student left /auth/confirm before pressing Confirm). A permanent
 *  "already handled" mark would strand them: iOS keeps routing the link to
 *  the app, and the app would ignore it until force-quit. */
export function isImmediateRepeat(
  prev: { url: string; at: number } | null,
  url: string,
  now: number,
): boolean {
  return prev !== null && prev.url === url && now - prev.at < UNIVERSAL_LINK_REPEAT_MS;
}
